/* @jsxImportSource @emotion/react */

import React, { useEffect, useRef, useState } from 'react'
import { css, keyframes } from '@emotion/react'
import { useInView } from 'react-intersection-observer'
import { useIntl } from 'react-intl'

import { SliteLogo, SuperSourceIcon } from '@slite/components'
import { SuperSource } from '@slite/search-client'

import { Button } from '../../../../components/Button/Button'
import { getCustomerQuote } from '../../../../components/customerQuotes'
import { GrainCard } from '../../../../components/GrainCard'
import { QuoteWithOrbit } from '../../../../components/QuoteWithOrbit'
import { Section } from '../../../../components/Section'
import { TextLayout } from '../../../../components/TextLayout'
import { WebsiteText } from '../../../../components/WebsiteText'
import { useRoutes } from '../../../../routes'
import { SMALL_BREAKPOINT } from '../../../../utils/breakpoints'
import aiSearchIllustration from '../../assets/ai-search.svg'
import { ScenarioButtons } from '../../Hero/ScenarioButtons'
import { AppInterface } from './AppInterface'
import {
  AI_SEARCH_SCENARIOS,
  AI_SEARCH_SOURCES,
  FEATURE_CARDS,
  scenarioPlayDurationMs,
} from './content'

// ─── Infinite-scroll source-icon marquee ────────────────────────────────
const ICON_SIZE = 50
const ICON_GAP = 24

const marqueeScroll = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`

const marqueeContainerCss = css`
  position: relative;
  overflow: hidden;
  /* Soft fade on both edges so icons drift in/out of view, not pop. */
  mask-image: linear-gradient(
    to right,
    transparent 0%,
    #000 8%,
    #000 92%,
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0%,
    #000 8%,
    #000 92%,
    transparent 100%
  );
`

const marqueeTrackCss = css`
  display: flex;
  width: max-content;
  /* Hover-pause is a nice touch for users skimming the row. */
  &:hover {
    animation-play-state: paused;
  }
  animation: ${marqueeScroll} 80s linear infinite;
  will-change: transform;
`

/* padding-right matches the gap so the two copies tile evenly across the
   -50% seam without a visible double-gap. */
const marqueeGroupCss = css`
  display: flex;
  align-items: center;
  gap: ${ICON_GAP}px;
  padding-right: ${ICON_GAP}px;
  flex-shrink: 0;
`

function SourceIconAtSize({ source }: { source: SuperSource }) {
  if (source === SuperSource.SLITE) {
    return (
      <SliteLogo
        variant="symbol"
        style={{ width: ICON_SIZE, height: ICON_SIZE }}
      />
    )
  }
  return <SuperSourceIcon sourceType={source} size={ICON_SIZE} />
}

// Pause between scenarios after the answer finishes streaming, before
// auto-advancing to the next one. Gives the viewer a beat to read.
const SCENARIO_HOLD_MS = 3500

export function SectionHomeAiSearch({
  title: titleOverride,
  description: descriptionOverride,
  hideCta,
  hideQuote,
  hideFeatureCards,
}: {
  title?: string
  description?: string
  hideCta?: boolean
  hideQuote?: boolean
  hideFeatureCards?: boolean
} = {}) {
  const intl = useIntl()
  const routes = useRoutes()
  const quote = getCustomerQuote('AndreFoekenNedapPerplexity', intl)
  const { ref: demoRef, inView } = useInView({
    threshold: 0.35,
    triggerOnce: false,
  })

  const [activeIdx, setActiveIdx] = useState(0)
  // playId drives the scenario-button progress fill; bumping it restarts the
  // fill animation. runKey is what we hand to SearchConversation to force a
  // re-run of its internal animation when the user clicks a button.
  const [playId, setPlayId] = useState(0)
  const [runKey, setRunKey] = useState(0)
  const activeScenario = AI_SEARCH_SCENARIOS[activeIdx]
  const playDurationMs = scenarioPlayDurationMs(activeScenario)
  // Full cycle length = play time + hold. Used by the button fill so it
  // empties exactly when we hand off to the next scenario.
  const cycleDurationMs = playDurationMs + SCENARIO_HOLD_MS

  // Auto-cycle: while the section is in view, advance to the next scenario
  // after one full cycle. Pauses (and resets to scenario 0) on scroll-out so
  // returning to the section restarts from the top.
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    // Reference activeIdx so the effect re-runs on scenario change even if
    // the next scenario happens to have the same cycle duration (in which
    // case cycleDurationMs alone wouldn't change between renders).
    void activeIdx
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current)
      advanceTimerRef.current = null
    }
    if (!inView) {
      setActiveIdx(0)
      return undefined
    }
    setPlayId((p) => p + 1)
    advanceTimerRef.current = setTimeout(() => {
      setActiveIdx((i) => (i + 1) % AI_SEARCH_SCENARIOS.length)
      setRunKey((k) => k + 1)
    }, cycleDurationMs)
    return () => {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current)
    }
  }, [inView, activeIdx, cycleDurationMs])

  const jumpToScenario = (i: number) => {
    if (i === activeIdx) return
    setActiveIdx(i)
    setRunKey((k) => k + 1)
  }

  const title =
    titleOverride ??
    intl.formatMessage({
      id: 'home.aiSearch.title',
      defaultMessage: 'Enterprise AI search for your teams and agents',
    })
  const description =
    descriptionOverride ??
    intl.formatMessage({
      id: 'home.aiSearch.description',
      defaultMessage:
        'Ask in Slite, Slack, your browser or any AI tool. One answer, cited, ranked by trust.',
    })
  const ctaLabel = intl.formatMessage({
    id: 'home.aiSearch.cta',
    defaultMessage: 'Explore AI Search',
  })

  return (
    <Section
      background="moon-dust-lighter"
      containerVerticalPadding={120}
      className="text-align-center"
    >
      <TextLayout
        type="centered"
        title={title}
        titleMaxWidth={730}
        illustration={<img src={aiSearchIllustration} alt="" />}
      />

      <div ref={demoRef}>
        <div
          css={css`
            position: sticky;
            top: 80px;
            z-index: 2;
          `}
        >
          <ScenarioButtons
            scenarios={AI_SEARCH_SCENARIOS}
            activeIdx={activeIdx}
            playId={playId}
            durationMs={cycleDurationMs}
            onSelect={jumpToScenario}
          />
        </div>
        <GrainCard
          css={css`
            padding: 64px 24px 0;
            height: 800px;
            @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
              padding: 12px 12px 0;
              height: 600px;
            }
          `}
        >
          <div style={{ maxWidth: 800, margin: '0 auto', height: '100%' }}>
            <AppInterface
              scenario={activeScenario}
              isActive={inView}
              runKey={runKey}
            />
          </div>
        </GrainCard>
      </div>

      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 32px;
          margin-top: 48px;
          padding: 0 4px;
          @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
            flex-direction: column;
          }
        `}
      >
        <WebsiteText
          variant="body-lg"
          className="shade-00"
          style={{ textAlign: 'start', maxWidth: 700 }}
        >
          {description}
        </WebsiteText>
        {!hideCta && (
          <Button
            href={routes.aiSearch()}
            variant="secondary"
            size="default"
            withChevron
          >
            {ctaLabel}
          </Button>
        )}
      </div>

      <div css={marqueeContainerCss} style={{ marginTop: 60 }}>
        <div css={marqueeTrackCss}>
          {[0, 1].map((copy) => (
            <div key={copy} css={marqueeGroupCss} aria-hidden={copy === 1}>
              {AI_SEARCH_SOURCES.map((source) => (
                <SourceIconAtSize key={`${copy}-${source}`} source={source} />
              ))}
            </div>
          ))}
        </div>
      </div>

      <div
        style={{
          display: hideFeatureCards ? 'none' : 'flex',
          gap: 24,
          marginTop: 48,
          flexWrap: 'wrap',
        }}
      >
        {FEATURE_CARDS.map((card) => (
          <GrainCard
            key={card.title}
            style={{
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'start',
              padding: '48px 48px 0',
              minWidth: 240,
              flex: 1,
              overflow: 'hidden',
            }}
          >
            <WebsiteText
              variant="smallTitle"
              className="margin-bottom-16 shade-00 font-family-title"
              style={{ maxWidth: 280, fontWeight: 500, fontSize: 28 }}
            >
              {card.title}
            </WebsiteText>
            <WebsiteText
              variant="body"
              className="margin-bottom-32 shade-00"
              style={{ maxWidth: 280 }}
            >
              {card.description}
            </WebsiteText>
            <img
              src={card.illustration}
              alt=""
              aria-hidden
              draggable={false}
              style={{
                display: 'block',
                width: 'calc(100% + 96px)',
                maxWidth: 'none',
                height: 'auto',
                aspectRatio: card.illustrationAspectRatio,
                margin: 'auto -48px 0',
              }}
            />
          </GrainCard>
        ))}
      </div>

      {!hideQuote && (
        <div className="flexbox flexbox-centered" style={{ marginTop: 80 }}>
          <QuoteWithOrbit
            name={quote.name}
            title={quote.title}
            testimonial={quote.testimonial}
            pictureUrl={quote.pictureUrl}
            companyImageUrl="https://cdn.sanity.io/images/dnsgqii5/production/c8e2593f1a2c52b10d8ceeffe9964206311e162e-200x200.png"
            className="grid-span-10-large grid-span-12"
          />
        </div>
      )}
    </Section>
  )
}
